import Icon from "@/components/Icon/Icon";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { defaultNS, useTranslation } from "@/lib/i18n";
import type { Table } from "@tanstack/react-table";

interface DataTablePaginationProps<TData> {
  table: Table<TData>;
  pageOptions?: number[];
  totalCount?: number;
  showTotalCount?: boolean;
}

export function DataTablePagination<TData>({
  table,
  pageOptions = [100, 200, 300, 400, 500],
  totalCount = 0,
  showTotalCount = false,
}: DataTablePaginationProps<TData>) {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "component.dataTable.pagination",
  });

  return (
    <div className="flex">
      {showTotalCount && (
        <div className="flex flex-initial items-center text-sm text-neutral-muted-on-fill pl-3">
          {t("rows", { count: totalCount })}
        </div>
      )}
      <div className="flex items-center px-2 ml-auto">
        <div className="flex items-center space-x-6 lg:space-x-8">
          <div className="flex items-center space-x-2">
            <p className="text-sm font-medium">{t("rowsPerPage")}</p>
            <Select
              value={`${table.getState().pagination.pageSize}`}
              onValueChange={(value) => {
                table.setPageSize(Number(value));
              }}
            >
              <SelectTrigger className="h-8 w-fit bg-background">
                <SelectValue
                  placeholder={table.getState().pagination.pageSize}
                />
              </SelectTrigger>
              <SelectContent side="top">
                {pageOptions.map((pageSize) => (
                  <SelectItem key={pageSize} value={`${pageSize}`}>
                    {pageSize}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex w-[100px] items-center justify-center text-sm font-medium">
            {t("pageOrder", {
              page: table.getState().pagination.pageIndex + 1,
              total: table.getPageCount() === 0 ? 1 : table.getPageCount(),
            })}
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              className="hidden p-0 w-8 h-8 lg:flex"
              onClick={() => table.setPageIndex(0)}
              disabled={!table.getCanPreviousPage()}
            >
              <span className="sr-only">{t("goToFirstPage")}</span>
              <Icon iconName="ChevronsLeft" />
            </Button>
            <Button
              variant="outline"
              className="p-0 w-8 h-8"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              <span className="sr-only">{t("goToPreviousPage")}</span>
              <Icon iconName="ChevronLeft" />
            </Button>
            <Button
              variant="outline"
              className="p-0 w-8 h-8"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              <span className="sr-only">{t("goToNextPage")}</span>
              <Icon iconName="ChevronRight" />
            </Button>
            <Button
              variant="outline"
              className="hidden p-0 w-8 h-8 lg:flex"
              onClick={() => table.setPageIndex(table.getPageCount() - 1)}
              disabled={!table.getCanNextPage()}
            >
              <span className="sr-only">{t("goToLastPage")}</span>
              <Icon iconName="ChevronsRight" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
